<template>
  <div class="text-center">
    <v-dialog
      z-index="9999"
      persistent
      v-model="dialog"
      dark
      width="600"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      transition="dialog-bottom-transition"
      overlay-opacity="0.9"
      overlay-color="black">
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          block
          large
          outlined
          color="rgba(255,255,255,0.5)"
          class="rounded-lg font-weight-bold grey--text text--lighten-1 hover-glow py-6"
          v-bind="attrs"
          v-on="on"
          @click="fetchEntries()">
          <v-icon left color="#00963f">mdi-pencil</v-icon> Korrektur
        </v-btn>
      </template>

      <v-card class="rounded-xl glass-card elevation-0" color="#1E1E1E">
        <v-toolbar flat color="transparent" class="pt-2 border-bottom-light">
          <v-btn icon @click="closeDialog" color="grey" v-if="$vuetify.breakpoint.xsOnly"><v-icon>mdi-arrow-left</v-icon></v-btn>
          <v-toolbar-title class="text-h6 font-weight-bold white--text pl-2">
            <v-icon color="#00963f" class="mr-2">mdi-playlist-edit</v-icon>
            Korrekturen
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-sheet color="rgba(0, 150, 63, 0.15)" class="rounded-lg px-3 py-1 d-flex align-center" style="border: 1px solid rgba(0, 150, 63, 0.3)">
            <v-icon x-small color="#00963f" class="mr-2">mdi-calendar</v-icon>
            <span class="primary-green--text font-weight-bold text-caption">{{ $moment(selectedDate).format("DD.MM.YYYY") }}</span>
          </v-sheet>
          <v-btn icon @click="closeDialog" color="grey" class="ml-2" v-if="!$vuetify.breakpoint.xsOnly"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>

        <v-card-text class="px-4 py-6 scroll-container">
          <v-btn
            block
            large
            color="transparent"
            class="rounded-lg mb-6 dashed-border grey--text text--lighten-1 btn-hover-slide"
            @click="openFormDialog"
            elevation="0"
            style="min-height: 60px">
            <v-icon left color="#00963f">mdi-plus-circle-outline</v-icon> Fehlenden Eintrag hinzufügen
          </v-btn>

          <div v-if="tableItems.length === 0" class="text-center py-10 grey--text d-flex flex-column align-center opacity-60">
            <v-avatar color="rgba(255,255,255,0.05)" size="80" class="mb-4">
              <v-icon size="40" color="grey darken-1">mdi-clock-outline</v-icon>
            </v-avatar>
            <div class="text-body-2">Keine Einträge für diesen Tag.</div>
          </div>

          <div v-else class="d-flex flex-column gap-3">
            <v-scale-transition group>
              <v-card
                v-for="(item, index) in tableItems"
                :key="item.time + index"
                class="rounded-lg pa-0 d-flex align-center border-light card-hover-lift"
                color="rgba(40, 40, 40, 0.6)"
                elevation="0">
                <div class="fill-height rounded-l-lg" :style="`width: 4px; background-color: ${getColorForChip(item)}; min-height: 80px;`"></div>

                <div
                  class="px-4 py-3 text-center d-flex flex-column justify-center"
                  style="min-width: 85px; border-right: 1px solid rgba(255, 255, 255, 0.05)">
                  <div class="text-h7 font-weight-bold white--text line-height-1">{{ item.time ? item.time.substring(0, 5) : "--:--" }}</div>
                  <div class="text-caption grey--text mt-1">Uhr</div>
                </div>

                <div class="flex-grow-1 px-4 py-2 overflow-hidden">
                  <div class="d-flex align-center mb-1">
                    <span class="font-weight-bold white--text text-body-1 mr-2">{{ mappedEntryTypes[item.realType] || item.type }}</span>
                    <v-icon x-small color="grey" v-if="item.correction === 'open'">mdi-pencil-circle</v-icon>
                  </div>

                  <div class="d-flex align-center">
                    <v-icon x-small color="grey darken-1" class="mr-1">mdi-domain</v-icon>
                    <div class="text-caption grey--text text-truncate">{{ item.studio }}</div>
                  </div>
                  <div v-if="item.correction === 'open'" class="mt-1 orange--text text-caption font-weight-bold">
                    <v-icon x-small color="orange" class="mr-1">mdi-alert-circle-outline</v-icon> Wartet auf Prüfung
                  </div>
                </div>

                <div class="d-flex flex-column px-2">
                  <v-btn icon small color="grey lighten-1" @click="editItem(item)" class="mb-1">
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon small color="red lighten-1" @click="deleteItem(item)" :disabled="item.correction === 'open'">
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </div>
              </v-card>
            </v-scale-transition>
          </div>
        </v-card-text>

        <v-divider class="border-light" v-if="!$vuetify.breakpoint.xsOnly"></v-divider>

        <v-card-actions class="justify-center py-4" v-if="!$vuetify.breakpoint.xsOnly">
          <v-btn color="#00963f" text large @click="closeDialog" class="font-weight-bold px-8">Schliessen</v-btn>
        </v-card-actions>
      </v-card>

      <v-dialog
        dark
        v-model="formDialog"
        max-width="400px"
        :fullscreen="$vuetify.breakpoint.xsOnly"
        :transition="$vuetify.breakpoint.xsOnly ? 'dialog-bottom-transition' : 'scale-transition'"
        overlay-opacity="0.8">
        <v-card color="#1E1E1E" :class="$vuetify.breakpoint.xsOnly ? 'rounded-0' : 'rounded-xl'" class="border-glow-card">
          <v-toolbar flat color="transparent" class="border-bottom-light">
            <v-toolbar-title class="white--text font-weight-bold pl-2">{{ formTitle }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
          </v-toolbar>

          <v-card-text class="px-4 px-sm-6 pt-6 d-flex flex-column align-center">
            <div class="mb-6 w-100 d-flex justify-center">
              <v-time-picker
                :key="timePickerKey"
                v-model="editedItem.time"
                format="24hr"
                color="#00963f"
                class="rounded-xl elevation-0 border-light time-picker-custom"
                :full-width="$vuetify.breakpoint.xsOnly"></v-time-picker>
            </div>

            <v-select
              v-model="editedItem.oldType"
              :items="[
                { text: 'Check-In (Start)', value: 'checkin', icon: 'mdi-login' },
                { text: 'Check-Out (Ende)', value: 'checkout', icon: 'mdi-logout' },
              ]"
              label="Typ auswählen"
              outlined
              filled
              dark
              rounded
              background-color="#2c2c2c"
              item-color="green"
              class="w-100 custom-select"
              :menu-props="{ offsetY: true, contentClass: 'rounded-xl' }">
              <template v-slot:selection="{ item }">
                <v-icon left color="#00963f">{{ item.icon }}</v-icon>
                <span class="font-weight-bold">{{ item.text }}</span>
              </template>
              <template v-slot:item="{ item }">
                <v-icon left small>{{ item.icon }}</v-icon>
                <span>{{ item.text }}</span>
              </template>
            </v-select>
          </v-card-text>

          <v-card-actions class="pb-6 px-6 pt-2">
            <v-btn text color="grey" @click="close" class="rounded-lg">Abbrechen</v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="#00963f"
              depressed
              large
              class="white--text rounded-lg px-8 font-weight-bold shadow-glow btn-hover-scale"
              @click="save"
              :loading="saving">
              Speichern
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="dialogDelete" max-width="400px">
        <v-card class="rounded-xl text-center pa-6 glass-card" color="#1E1E1E">
          <v-sheet color="rgba(244, 67, 54, 0.1)" class="rounded-circle d-inline-flex pa-4 mb-4">
            <v-icon size="40" color="#ff5252">mdi-delete-alert-outline</v-icon>
          </v-sheet>

          <h3 class="text-h7 white--text font-weight-bold mb-2">Eintrag löschen?</h3>
          <div class="grey--text text-body-2 mb-6">Möchtest du diesen Eintrag wirklich unwiderruflich entfernen?</div>
          <v-row dense>
            <v-col cols="6">
              <v-btn block large text color="grey" @click="closeDelete" class="rounded-lg">Abbrechen</v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn block large color="#ff5252" depressed class="rounded-lg white--text shadow-red" @click="deleteItemConfirm">Löschen</v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-dialog>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import { mapState } from "vuex"

export default {
  name: "DemandTimeEntryEdit",
  props: {
    selectedDate: {
      required: true,
    },
  },
  data() {
    return {
      timePickerKey: 0,
      saving: false,
      editIndex: -1,
      defaultItem: {
        time: "",
        type: null,
      },
      editedItem: {
        time: "",
        type: null,
      },
      formDialog: false,
      dialogDelete: false,
      mappedEntryTypes: {
        sick: "Krank",
        holiday: "Urlaub",
        checkin: "Check-In",
        checkout: "Check-Out",
        pause: "Pause",
        pauseend: "Pause fertig",
        error: "Fehler (Checkin ohne Checkout)",
        error1: "Fehler (2 Check-Ins)",
        error2: "Fehler (2 Check-Outs)",
        error3: "Fehler (Checkout ohne Checkin)",
        deleteCorrectionOpen: "Löschen (Offen)",
        checkinCorrectionOpen: "Check-In (Offen)",
        checkinCorrectionDenied: "Check-In (Abgelehnt)",
        checkinCorrectionApproved: "Check-In (Genehmigt)",
        checkoutCorrectionOpen: "Check-Out (Offen)",
        checkoutCorrectionDenied: "Check-Out (Abgelehnt)",
        checkoutCorrectionApproved: "Check-Out (Genehmigt)",
      },
      dialog: false,
      entries: [],
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    formTitle() {
      return this.editIndex === -1 ? "Neuer Eintrag" : "Eintrag bearbeiten"
    },
    tableItems() {
      let items = []
      this.entries.forEach((entry) => {
        items.push({
          correction: entry.correction || null,
          oldType: entry.oldType,
          realType: entry.correctedType || entry.type,
          time: entry.time,
          type: this.mappedEntryTypes[entry.type],
          studio: this.storeStudios.find((stud) => stud._id === entry.studio)?.name || "Unbekannt", // Safe check added
        })
      })
      // Sortierung nach Zeit für bessere Übersicht
      return items.sort((a, b) => (a.time > b.time ? 1 : -1))
    },
  },
  methods: {
    openFormDialog() {
      this.timePickerKey += 1
      this.editIndex = -1
      this.editedItem = { time: "", type: null }
      this.formDialog = true
    },
    getColorForChip(item) {
      if (
        ["error", "error1", "error2", "error3", "checkinCorrectionDenied", "checkoutCorrectionDenied", "deleteCorrectionOpen"].includes(item.realType)
      ) {
        return "#ff5252" // Red
      }
      if (["checkinCorrectionOpen", "checkoutCorrectionOpen"].includes(item.realType) || item.correction === "open") {
        return "#FF9800" // Orange
      }
      if (["checkinCorrectionApproved", "checkoutCorrectionApproved"].includes(item.realType)) {
        return "#00963f" // Green
      }
      return "#00963f" // Green Default for normal entries
    },
    async fetchEntries() {
      this.entries = []
      let response = await api.fetchTimeTrackerDemandCorrection(this.$moment(this.selectedDate).format("YYYY-MM-DD"))
      if (response.attendance && response.attendance[this.$moment(this.selectedDate).format("YYYY-MM-DD")]) {
        this.entries = response.attendance[this.$moment(this.selectedDate).format("YYYY-MM-DD")]
      }
    },
    editItem(item) {
      this.timePickerKey += 1
      this.editIndex = this.entries.findIndex((entry) => entry.time === item.time)
      this.editedItem = Object.assign({}, item)
      // Fix deep copy issues
      this.editedItem.beforeCorrectionType = JSON.parse(JSON.stringify(this.editedItem.oldType || null))
      this.editedItem.beforeCorrectionTime = JSON.parse(JSON.stringify(this.editedItem.time))
      this.formDialog = true
    },

    deleteItem(item) {
      this.editIndex = this.entries.findIndex((entry) => entry.time === item.time)
      this.editedItem = Object.assign({}, item)
      this.dialogDelete = true
    },

    async deleteItemConfirm() {
      try {
        await api.deleteAttendanceEntry({
          date: this.$moment(this.selectedDate).format("YYYY-MM-DD"),
          time: this.editedItem.time,
        })
      } catch (e) {
        console.log(e)
        this.$toast.error("Löschen fehlgeschlagen")
      }
      this.closeDelete()
      this.fetchEntries()
    },

    close() {
      this.timePickerKey += 1
      this.formDialog = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editIndex = -1
      })
    },

    closeDelete() {
      this.dialogDelete = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editIndex = -1
      })
    },

    async save() {
      this.saving = true
      if (!this.editedItem.time) {
        this.saving = false
        return this.$toast.error("Bitte Zeit eingeben")
      }
      if (!this.editedItem.oldType) {
        this.saving = false
        return this.$toast.error("Bitte Typ auswählen")
      }

      // Basic check for time format length, append seconds if missing
      if (this.editedItem.time.length < 6) {
        this.editedItem.time = this.editedItem.time + ":00"
      }

      try {
        if (this.editIndex > -1) {
          await api.updateExistingAttendanceEntry({
            date: this.$moment(this.selectedDate).format("YYYY-MM-DD"),
            correction: this.editedItem,
          })
        } else {
          await api.createNewAttendanceCorrection({
            date: this.$moment(this.selectedDate).format("YYYY-MM-DD"),
            correction: this.editedItem,
          })
        }
        this.$toast.success("Gespeichert")
      } catch (e) {
        this.$toast.error("Korrektur fehlgeschlagen (Duplikat?)")
      }

      this.saving = false
      this.fetchEntries()
      this.close()
    },
    closeDialog() {
      this.$emit("fetchEntries")
      this.dialog = false
    },
  },
}
</script>

<style scoped lang="scss">
.primary-green--text {
  color: #00963f !important;
}

/* Glass & Borders */
.glass-card {
  background: rgba(30, 30, 30, 0.85) !important;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.border-bottom-light {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}
.border-light {
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}

/* Dashed Button */
.dashed-border {
  border: 1px dashed rgba(255, 255, 255, 0.2) !important;
  background-color: rgba(255, 255, 255, 0.02) !important;
  transition: all 0.3s ease;

  &:hover {
    border-color: #00963f !important;
    background-color: rgba(0, 150, 63, 0.05) !important;
    color: #00963f !important;

    .v-icon {
      transform: scale(1.1);
    }
  }
}

/* Shadows */
.shadow-glow {
  box-shadow: 0 0 15px rgba(0, 150, 63, 0.4) !important;
}
.shadow-red {
  box-shadow: 0 4px 15px rgba(244, 67, 54, 0.4) !important;
}
.hover-glow:hover {
  color: #00963f !important;
  background: rgba(0, 150, 63, 0.1);
}

/* Typography */
.line-height-1 {
  line-height: 1;
}
.gap-3 {
  gap: 12px;
}
.w-100 {
  width: 100%;
}

/* Animations */
.card-hover-lift {
  transition: transform 0.2s ease, background-color 0.2s ease;
  &:hover {
    transform: translateY(-2px);
    background-color: rgba(50, 50, 50, 0.8) !important;
  }
}
.btn-hover-scale:hover {
  transform: scale(1.02);
}

/* Timepicker Customization (Deep Selectors) */
::v-deep .v-picker__body {
  background: transparent !important;
}
::v-deep .v-time-picker-clock {
  background: #2c2c2c !important;
}
::v-deep .theme--dark.v-btn.v-btn--has-bg {
  background-color: #2c2c2c;
}

.scroll-container {
  max-height: 70vh;
  overflow-y: auto;
}
</style>
